import { useContext, useState } from "react";
import SelectPagesContainer from "./SelectPagesContainer";
import PreviewAndExtractComponent from "./PreviewAndExtractComponent";
import { Button, Divider, Grid, IconButton } from "@mui/material";
import IconsColor from "../../../Utils/IconsColor";
import styles from "../../../Styles/ComponentStyles/AIPdfExtractor/AIPdfExtractor.module.scss"
import GlobalButton from "../../../Utils/GlobalUtilsComponents/GlobalButton";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ProfileContext, ProfileContextPayload } from "../../../Context/ProfileDetailsContext";
import { UserContext, UserContextPayload } from "../../../Context/UserDetailContext";
import { useSnackbar } from "../../GlobalSnackbar/GlobalSnackbar";
import PulsatingLoader from "../../GlobalLoader/PulsatingLoader";

const ExtractionStepContainer = () => {

    const {
        setFlagImportBtnClicked,
        extractedFileData,
        selectedPages,
        extractPdfDataAPI,
        checkPdfExtractionStatusAPI,
        setExtractedTablesData,
        extractedTablesData,
        selectedFields,
        editableData,
        selectedColumns,
        setExtractedTextData,
        extractedTextData,
        setFlagOpenExtractDataDialog
    } = useContext(ProfileContext) as ProfileContextPayload

    const { showSnackbar } = useSnackbar();

    const {
        setFlagLoader,
    } = useContext(UserContext) as UserContextPayload

    const [step, setStep] = useState(1);
    const [isExtracting, setIsExtracting] = useState(false);
    const [isUILoading, setIsUILoading] = useState(false);

    const steps: any = [
        {
            stepNumber: 1,
            name: "Select Pages",
            flagDisabled: false
        },
        {
            stepNumber: 2,
            name: "Preview & Extract Data",
            flagDisabled: extractedTablesData?.length < 0
        },
    ];

    const handleStepClick = (stepNumber: number) => {
        if (!steps[stepNumber - 1].flagDisabled) {
            setStep(stepNumber);
        }
    };

    const handleExtractTablesBtnClick = async () => {
        setIsExtracting(true); // Show the pulsating loader

        const payload = {
            file_id: extractedFileData?.file_id,
            page_number: selectedPages
        };

        try {
            const response = await extractPdfDataAPI(payload);
            if (response?.status) {
                const logId = response?.data?.[0]?.logId;
                if (logId) {
                    await checkPdfExtractionStatusAPI(logId); // First call immediately
                    pollExtractionStatus(logId);
                }
            } else {
                showSnackbar(response?.error?.message || "Something went wrong", "error");
                setIsExtracting(false);
            }
        } catch (error) {
            console.log("Error in extracting tables: ", error);
            setIsExtracting(false);
        }
    };

    const pollExtractionStatus = (log_id: string) => {
        const interval = setInterval(async () => {
            try {
                const statusResponse = await checkPdfExtractionStatusAPI(log_id);
                if (statusResponse?.extraction_status?.toLowerCase() === "completed") {
                    clearInterval(interval);
                    setExtractedTablesData(statusResponse?.data);
                    showSnackbar("Extraction completed successfully", "success");
                    setIsExtracting(false);
                    setStep(2);
                }

            } catch (error) {
                console.log("Error while checking extraction status: ", error);
            }
        }, 20000); // Poll every 20 seconds
    };

    const handleExtractData = () => {
        const newExtractedData: any = {};

        // Go through each selected table
        Object.entries(selectedFields).forEach(([tableId, fieldValue]) => {
            // Find the table in extractedTablesData
            const [pageIndexStr, tableIndexStr] = tableId.split("-");
            const pageIndex = parseInt(pageIndexStr);
            const tableIndex = parseInt(tableIndexStr);
            const pageData = extractedTablesData[pageIndex]; // ✅ uses the updated edited data

            if (!pageData?.extractedData?.tables?.[tableIndex]) return;

            const table = pageData.extractedData.tables[tableIndex];
            const selectedCols = selectedColumns[tableId] || {};
            const selectedHeaders = Object.entries(selectedCols)
                .filter(([_, isSelected]) => isSelected)
                .map(([header]) => header);

            if (selectedHeaders.length === 0) return;

            if (!newExtractedData[fieldValue]) {
                newExtractedData[fieldValue] = [];
            }

            // Extract and clean data row-wise
            table.table_data.forEach((row: any) => {
                const rowData = selectedHeaders.map((header) =>
                    (row[header] || "").toString().replace(/,/g, "")
                );
                newExtractedData[fieldValue].push(rowData.join("\t"));
            });
        });

        // Convert arrays into newline-separated strings
        const formattedData: any = {};
        Object.entries(newExtractedData).forEach(([field, rows]: any) => {
            formattedData[field] = rows.join("\n");
        });

        // Push to state
        setExtractedTextData({
            IULFeeDetails: formattedData["Field1"] || "",
            IULAccountValueDetail: formattedData["Field2"] || "",
            IULDeathBenifits: formattedData["Field3"] || ""
        });

        setFlagOpenExtractDataDialog(false);
    };

    return (
        <>
            {isExtracting && <PulsatingLoader />}

            <Grid container className={styles.extractionContainer} gap={1}>
                {/* Extraction steps */}
                <Grid
                    item
                    xs={12}
                    className={styles.headerContainer}
                >
                    <Grid container alignItems="center">
                        {/* Left section with back button and filename */}
                        <Grid item xs={3}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item>
                                    <IconButton
                                        onClick={() => setFlagImportBtnClicked(false)}
                                        className={styles.backButton}
                                    >
                                        <IconsColor iconName="backArrow" />
                                    </IconButton>
                                </Grid>
                                <Grid item width={"350px"}>
                                    <span className={styles.fileNameText}>{extractedFileData?.file_name}</span>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Center section with steps indicator */}
                        <Grid item xs={6}>
                            <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                {steps.map((currentStep: any, index: any) => (
                                    <>
                                        {/* Step Button */}
                                        <Grid item key={currentStep.stepNumber}>
                                            <Button
                                                disabled={currentStep.flagDisabled}
                                                variant="text"
                                                className={
                                                    `${styles.stepContainer} 
                                                    ${step === currentStep.stepNumber
                                                        ? styles.activeStep
                                                        : step > currentStep.stepNumber
                                                            ? styles.completedStep
                                                            : ""
                                                    }`
                                                }
                                                onClick={() => handleStepClick(currentStep.stepNumber)}
                                                sx={{
                                                    textTransform: "unset",
                                                    fontWeight: "600",
                                                    color: "white",
                                                    "&.Mui-disabled": {
                                                        color: "rgba(213, 213, 213) !important"
                                                    }
                                                }}
                                            >
                                                <span
                                                    className={styles.circle}
                                                    style={{
                                                        backgroundColor: step !== currentStep.stepNumber ? '#c0c0c0' : '',
                                                        color: step !== currentStep.stepNumber ? '#6B6767' : ''
                                                    }}
                                                >
                                                    {currentStep.stepNumber}
                                                </span>
                                                <span className={styles.stepText}>{currentStep.name}</span>
                                            </Button>
                                        </Grid>

                                        {/* Step Divider */}
                                        {index < steps.length - 1 && (
                                            <Grid item>
                                                <div className={styles.stepDivider}></div>
                                            </Grid>
                                        )}
                                    </>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Right section with extract button */}
                        <Grid item xs={3}>
                            <Grid container justifyContent="flex-end">
                                <Grid item>
                                    <GlobalButton
                                        value={step === 2 ? "Extract Data" : "Extract tables from selected pages"}
                                        size="medium"
                                        className={styles.extractTablesBtn}
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => step === 2 ? handleExtractData() : handleExtractTablesBtnClick()}
                                        disabled={step === 2
                                            ? !Object.values(selectedColumns).some(obj => Object.keys(obj).length > 0) // Button enabled when at least one column is selected
                                            : selectedPages.length === 0}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Content section */}
                <Grid item xs={12}>
                    {step === 1 && <SelectPagesContainer />}
                    {step === 2 && <PreviewAndExtractComponent />}
                </Grid>
            </Grid>
        </>
    )
}

export default ExtractionStepContainer